import {
  DestroyRef,
  Directive,
  OnInit,
  Signal,
  computed,
  inject,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";

import { AlertService } from "../../services/alert.service";
import { TranslationService } from "../../services/translation.service";
import {
  LanguageConfig,
  TRANSLATION_LANGUAGES,
  TranslationLanguage,
  TranslationResults,
} from "../../types/translation.types";
import { LOCALE_KEY_SUFFIX, isHtmlEmpty } from "./translatable.utils";

export interface TargetLanguageOption {
  code: TranslationLanguage;
  label: string;
  selected: boolean;
}

const BODY_BASE =
  "border rounded-2xl p-3.5 bg-white transition-colors focus-within:shadow-[0_0_0_3px_rgba(53,123,191,0.18)]";
const CHIP_BASE =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-alternate text-[13px] font-medium leading-none cursor-pointer transition-colors";
const CHIP_SELECTED = `${CHIP_BASE} bg-mty-primary/10 text-mty-primary border-mty-primary`;
const CHIP_IDLE = `${CHIP_BASE} bg-white border-black/10 hover:bg-mty-primary/5 hover:border-mty-primary/35`;

/**
 * Base class for translatable Formly field types (input, ckeditor, …).
 *
 * Owns the multi-locale data flow, validation state, and translation
 * orchestration; concrete subclasses provide only the editor element
 * through `<app-translatable-shell>` content projection.
 */
@Directive()
export abstract class TranslatableFieldBase
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  protected readonly translationService = inject(TranslationService);
  protected readonly alertService = inject(AlertService);
  protected readonly destroyRef = inject(DestroyRef);

  readonly languages: readonly LanguageConfig[] = TRANSLATION_LANGUAGES;
  readonly activeTab = signal<TranslationLanguage>("nl");
  readonly isTranslating = signal<boolean>(false);

  /**
   * Bumped on every formControl event AND on every `setFieldValue`
   * (the latter suppresses `valueChanges` to keep Formly's writeback
   * from racing the validator). Status-dependent methods read this
   * signal so OnPush templates — both the field's own and the shell —
   * re-render when underlying form state or model values change.
   */
  private readonly statusTick = signal(0);

  private readonly localeSelected = signal<
    Record<TranslationLanguage, boolean>
  >({
    nl: true,
    en: true,
    de: true,
    fr: true,
    pt: true,
  });

  readonly targetLanguages: Signal<TargetLanguageOption[]> = computed(() => {
    const active = this.activeTab();
    const selected = this.localeSelected();
    return this.languages
      .filter((l) => l.code !== active)
      .map((l) => ({
        code: l.code,
        label: l.name,
        selected: selected[l.code],
      }));
  });

  ngOnInit(): void {
    this.activeTab.set(
      (this.props["sourceLanguage"] || "nl") as TranslationLanguage
    );

    this.formControl?.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.statusTick.update((n) => n + 1));
  }

  /**
   * Override to normalise the raw editor value before it hits the model.
   * Default: identity. CKEditor strips placeholder HTML so empty
   * paragraphs don't reach the BE.
   */
  protected normaliseValue(value: string): string | null {
    return value;
  }

  protected getFieldName(language: TranslationLanguage): string {
    return (this.props["fieldBaseName"] || "") + LOCALE_KEY_SUFFIX[language];
  }

  getFieldValue(language: TranslationLanguage): string {
    this.statusTick();
    const model = (this.model ?? {}) as Record<string, unknown>;
    return (model[this.getFieldName(language)] as string) || "";
  }

  /**
   * Mutate the model FIRST, then sync the formControl. The
   * `atLeastOneLocale` validator reads from `field.model`, and
   * `FormControl.setValue()` runs validators BEFORE emitting
   * `valueChanges` — updating the model afterwards leaves the validator
   * looking at a stale value on a fresh paste. `{ emitEvent: false }`
   * keeps Formly's `valueChanges` writeback (formly.field.mjs:248) from
   * pushing a stale `control.value` back over the model.
   */
  setFieldValue(language: TranslationLanguage, value: string): void {
    const fieldName = this.getFieldName(language);
    const normalised = this.normaliseValue(value);

    if (this.model) {
      (this.model as Record<string, unknown>)[fieldName] = normalised;
    }

    if (this.formControl) {
      this.formControl.markAsDirty();
      if (fieldName === String(this.field.key ?? "")) {
        this.formControl.setValue(normalised, { emitEvent: false });
      } else {
        this.formControl.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.statusTick.update((n) => n + 1);
  }

  setActiveTab(language: TranslationLanguage): void {
    this.activeTab.set(language);
  }

  setLocaleSelected(language: TranslationLanguage, selected: boolean): void {
    this.localeSelected.update((s) => ({ ...s, [language]: selected }));
  }

  isLocaleEmpty(language: TranslationLanguage): boolean {
    return isHtmlEmpty(this.getFieldValue(language));
  }

  hasSourceContent(): boolean {
    return !this.isLocaleEmpty(this.activeTab());
  }

  isInvalid(): boolean {
    this.statusTick();
    const c = this.formControl;
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  markTouched(): void {
    this.formControl?.markAsTouched();
  }

  bodyClass(): string {
    return this.isInvalid()
      ? `${BODY_BASE} border-mty-danger`
      : `${BODY_BASE} border-black/10 focus-within:border-mty-primary`;
  }

  chipClass(code: TranslationLanguage): string {
    return this.activeTab() === code ? CHIP_SELECTED : CHIP_IDLE;
  }

  translateInline(): void {
    const sourceLanguage = this.activeTab();
    const sourceText = this.getFieldValue(sourceLanguage);

    if (isHtmlEmpty(sourceText)) {
      this.alertService.error(
        "Please enter content in the source language before translating."
      );
      return;
    }

    const selectedLangs = this.targetLanguages().filter((l) => l.selected);
    if (selectedLangs.length === 0) {
      this.alertService.error(
        "Please select at least one target language from the dropdown."
      );
      return;
    }

    this.isTranslating.set(true);

    this.translationService
      .translateToMultiple(
        sourceText,
        selectedLangs.map((l) => l.code),
        sourceLanguage
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results: TranslationResults) => {
          // Empty translatedText on a successful response means the
          // backend decided no translation was needed (e.g. cognate
          // words) — fall back to the source text so the target locale
          // doesn't stay empty.
          for (const result of results.results) {
            if (!result.success) continue;
            this.setFieldValue(
              result.targetLanguage,
              result.translatedText || sourceText
            );
          }

          if (results.failureCount === 0) {
            this.alertService.success(
              `Successfully translated to ${results.successCount} language(s).`
            );
          } else if (results.successCount > 0) {
            this.alertService.error(
              `Translated to ${results.successCount} language(s). ${results.failureCount} translation(s) failed.`
            );
          } else {
            this.alertService.error(
              "All translations failed. Please try again."
            );
          }

          this.isTranslating.set(false);
        },
        error: (error) => {
          console.error("Translation error:", error);
          this.alertService.error("Translation failed. Please try again.");
          this.isTranslating.set(false);
        },
      });
  }
}
